import { useCallback, useRef } from 'react'
import { useOidc } from '@/features/oidc/oidc'
import { useUserInactivityStore } from './user-inactivity-store'
import { useBroadcastMessage } from './use-broadcast-message'
import { DEFAULT_DEBOUNCE_DELAY } from './constants'
import { DEFAULT_INACTIVITY_TIME } from './constants'
import { RegisteredRouter } from '@tanstack/react-router'

export const useUserInactivityManager = ({
  inactivityTime = DEFAULT_INACTIVITY_TIME,
  debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  router,
}: {
  inactivityTime?: number
  debounceDelay?: number
  router?: RegisteredRouter
} = {}) => {
  const { logout } = useOidc()
  const setExpireTime = useUserInactivityStore((state) => state.setExpireTime)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const onLogout = useCallback(async () => {
    const location = router?.buildLocation({
      to: '/inactivity-logout',
      search: { redirect_uri: window.location.href },
    })

    await logout?.({
      redirectTo: 'specific url',
      url: location?.href ?? window.location.href,
    })
  }, [logout, router])

  const onReset = useCallback(() => {
    const newExpireTime = Date.now() + inactivityTime
    setExpireTime(newExpireTime)
  }, [setExpireTime, inactivityTime])

  const { broadcast: broadcastLogout } = useBroadcastMessage({
    channel: 'logout_channel',
    message: 'logout',
    onMessage: onLogout,
  })

  const { broadcast: broadcastReset } = useBroadcastMessage({
    channel: 'reset_channel',
    message: 'reset',
    onMessage: onReset,
  })

  const resetUserInactivityTimer = useCallback(() => {
    const expireTime = useUserInactivityStore.getState().expireTime

    if (expireTime === null) {
      broadcastReset()
      onReset()
      return
    }

    const lastResetTime = expireTime ? expireTime - inactivityTime : null

    if (lastResetTime && Date.now() > lastResetTime + debounceDelay) {
      broadcastReset()
      onReset()
    }
  }, [broadcastReset, onReset, debounceDelay, inactivityTime])

  const debouncedResetUserInactivityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      resetUserInactivityTimer()
    }, debounceDelay)
  }, [resetUserInactivityTimer, debounceDelay])

  const checkUserInactivityTimer = useCallback(() => {
    const expireTime = useUserInactivityStore.getState().expireTime

    if (expireTime && expireTime < Date.now()) {
      broadcastLogout()
      onLogout()
    }
  }, [broadcastLogout, onLogout])

  return {
    resetUserInactivityTimer: debouncedResetUserInactivityTimer,
    checkUserInactivityTimer,
  }
}
